import React, { useMemo, useCallback } from "react";
import { Grid, Box, Card, CardMedia, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import imageNotFound from "../../../assets/card-image/product-not-found.jpg"
import ProductListSkeleton from "../../DASHBOARD/ProductListSkeleton";
import { capitalizeFirst, extractDiscount } from "../../../hooks/utilsFn/utilsFn";
const ProductCardsList = ({ selectedCategory, products, loading }) => {
  const navigate = useNavigate();

  // Memoized filtered products
  const filteredProducts = useMemo(() => {
    if (!products?.length) return [];

    // If no category selected or selectedCategory is empty, show all products
    if (!selectedCategory || selectedCategory.length === 0) return products;

    // Filter products based on selected categories
    return products.filter(product =>
      selectedCategory.includes(product?.node?.category) ||
      selectedCategory.includes(product?.node?.categoryId)
    );
  }, [products, selectedCategory]);

  // Navigation handler
  const handleProductClick = useCallback((productId) => {
    navigate(`/card-details/${productId}`);
  }, [navigate]);

  // Extract product data helper
  const getProductData = useCallback((product) => ({
    id: product?.node?.id,
    slug: product?.node?.slug,
    name: product?.node?.name,
    logoUrl: product?.node?.productImages?.[0]?.url,
    discount: product?.node?.denominations?.[0]?.value,
    dicsountType: product?.node?.denominations?.[0]?.cashbackOrDiscount,
    category: product?.node?.category
  }), []);

  // No products found component
  const NoProductsFound = useMemo(() => (
    <Grid
      item
      xs={12}
      height="60vh"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Box
        sx={{
          textAlign: "center",
          padding: "30px 40px",
          borderRadius: "16px",
          backdropFilter: "blur(8px)",
          maxWidth: "420px",
        }}
      >
        <Box
          sx={{
            width: "45px",
            height: "45px",
            margin: "0 auto 16px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "large",
          }}
        >
          <RemoveShoppingCartIcon sx={{ color: "white" }} />
        </Box>

        <Typography
          sx={{
            fontFamily: "Be Vietnam Pro",
            fontSize: "20px",
            fontWeight: 600,
            color: "#333",
          }}
        >
          No Products Found
        </Typography>

        <Button
          variant="contained"
          onClick={() => navigate("/")}
          sx={{
            marginTop: "16px",
            textTransform: "none",
            borderRadius: "8px",
            background: "primaryDark.main",
            padding: "8px 18px",
            fontWeight: 500,
            fontFamily: "Be Vietnam Pro",
            width: "70%",
            fontSize: "13px",
            "&:hover": {
              background: "primary.main",
            },
          }}
        >
          Go to Home
        </Button>
      </Box>
    </Grid>
  ), [navigate]);


  const ProductCard = useMemo(() => {
    return ({ product }) => {
      const { id, slug, name, logoUrl, dicsountType } = getProductData(product);
      const discount = extractDiscount(product?.node?.denominations?.[0]);
      const symbol = dicsountType === "CASHBACK" ? "%" : "%"

      return (
        <Grid
          item
          xs={6}
          sm={4}
          md={4}
          lg={3}
          xl={3}
          key={id}
          onClick={() => handleProductClick(slug)}
        >
          <Box
            elevation={0}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              cursor: "pointer",
              mt: 1,

            }}
          >
            <Card
              sx={{
                borderRadius: '10px',
                aspectRatio: {
                  xs: "1 / 0.7",
                  sm: "4 / 3",
                  md: "4 /2.5",
                },
                boxShadow: "none",
                backgroundColor: 'transparent',
                transition: '0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                },
              }}
            >
              <CardMedia
                component="img"
                image={logoUrl || imageNotFound}
                alt={name}
                loading="lazy"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 2,

                }}
              />
            </Card>

            <Box sx={{ mt: 1, px: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "11px", md: "13.5px", },
                  color: "#000",
                  whiteSpace: { xs: "wrap", md: "nowrap" },
                  overflow: "hidden",
                  textOverflow: { xs: "none", md: "ellipsis" },
                  textAlign: "start",
                  fontFamily: "Be Vietnam Pro",
                }}
              >
                {name}
              </Typography>
            </Box>

            <Box sx={{ mt: 0.5, px: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "11.5px",
                  color: "#007411",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "start",
                  fontFamily: "Be Vietnam Pro",
                }}
              >
                {capitalizeFirst(`${discount} ${symbol} ${dicsountType}`)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      );
    };
  }, [getProductData, handleProductClick]);

  if (loading) {
    return <ProductListSkeleton />;
  }

  // Early return if no products
  if (!filteredProducts.length) {
    return (
      <Grid container spacing={2} mb={4}>
        {NoProductsFound}
      </Grid>
    );
  }

  return (
    <Grid container spacing={1} mb={4}>
      {filteredProducts.map((product) => (
        <ProductCard key={product?.node?.id} product={product} />
      ))}
    </Grid>
  );
};

// Memoize the entire component
export default React.memo(ProductCardsList);
